import { CURRENCY_STORAGE_KEY, SELECTABLE_CURRENCIES, type SelectableCurrency } from "@/lib/constants";
import { appLocale, detectedCurrency, formatChipValueCurrency as fmtChipValueCurrency, formatCurrency as fmtCurrency, getCurrencySymbol } from "@/lib/formatting";
import { useEffect, useMemo, useState } from "react";

function isSelectableCurrency(value: string): value is SelectableCurrency {
  return SELECTABLE_CURRENCIES.includes(value as SelectableCurrency);
}

const defaultSelectedCurrency: SelectableCurrency = isSelectableCurrency(detectedCurrency) ? detectedCurrency : "EUR";

export interface UseCurrencyReturn {
  selectedCurrency: SelectableCurrency;
  setSelectedCurrency: (currency: SelectableCurrency) => void;
  currencySymbol: string;
  formatCurrency: (amount: number) => string;
  formatChipValueCurrency: (amount: number) => string;
}

export function useCurrency(): UseCurrencyReturn {
  const [selectedCurrency, setSelectedCurrency] = useState<SelectableCurrency>(() => {
    if (typeof window === "undefined") {
      return defaultSelectedCurrency;
    }
    const stored = window.localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (stored && isSelectableCurrency(stored)) {
      return stored;
    }
    return defaultSelectedCurrency;
  });

  useEffect(() => {
    window.localStorage.setItem(CURRENCY_STORAGE_KEY, selectedCurrency);
  }, [selectedCurrency]);

  const currencySymbol = useMemo(() => getCurrencySymbol(selectedCurrency), [selectedCurrency]);

  const formatCurrency = (amount: number): string => fmtCurrency(amount, selectedCurrency);
  const formatChipValueCurrency = (amount: number): string => fmtChipValueCurrency(amount, selectedCurrency);

  return {
    selectedCurrency,
    setSelectedCurrency,
    currencySymbol,
    formatCurrency,
    formatChipValueCurrency,
  };
}

export { isSelectableCurrency };
